import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { Sparkles, Loader2, Trash2, ExternalLink, Send, RefreshCw } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const BlogAdmin = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [posts, setPosts] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [topic, setTopic] = useState('');
  const [autoPublish, setAutoPublish] = useState(true);
  const [postToSocials, setPostToSocials] = useState(true);

  const fetchPosts = async () => {
    try {
      const r = await axios.get(`${API}/admin/blog/posts`, { headers, timeout: 15000 });
      setPosts(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error('Failed to load posts');
    }
  };

  const fetchChannels = async () => {
    try {
      const r = await axios.get(`${API}/admin/blog/buffer-channels`, { headers, timeout: 8000 });
      setChannels(r.data?.channels || []);
    } catch (e) {
      // Buffer offline / rate-limited — non-fatal, just leaves panel empty
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchPosts();
      setLoading(false);
      // Buffer channels load independently — don't gate UI on it
      fetchChannels();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await axios.post(
        `${API}/admin/blog/generate`,
        { topic: topic || null, auto_publish: autoPublish, post_to_socials: postToSocials },
        { headers, timeout: 180000 },
      );
      const title = r.data?.post?.title || 'post';
      toast.success(
        `Generated: ${title}. ${postToSocials ? 'Image + socials processing in background — refresh to see status.' : 'Image generating in background.'}`,
        { duration: 8000 },
      );
      setTopic('');
      await fetchPosts();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const code = e.code;
      if (code === 'ECONNABORTED') {
        toast.error('Request timed out. Try a shorter topic, or check backend logs.');
      } else if (detail) {
        toast.error(detail, { duration: 12000 });
      } else if (e.message) {
        toast.error(`Generation failed: ${e.message}`, { duration: 12000 });
      } else {
        toast.error('Generation failed (unknown error)');
      }
    } finally {
      setGenerating(false);
    }
  };

  const publish = async (postId) => {
    if (!confirm('Publish + post to all socials now?')) return;
    try {
      await axios.post(`${API}/admin/blog/posts/${postId}/publish`, null, {
        headers,
        params: { post_to_socials: true },
      });
      toast.success('Published & queued to socials');
      await fetchPosts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Publish failed');
    }
  };

  const remove = async (postId, title) => {
    if (!confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/blog/posts/${postId}`, { headers });
      toast.success('Deleted');
      await fetchPosts();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const retrySocials = async (postId) => {
    try {
      toast.info('Retrying socials…');
      const r = await axios.post(`${API}/admin/blog/posts/${postId}/retry-socials`, null, {
        headers,
        timeout: 90000,
      });
      const social = r.data?.social_results || [];
      const okCount = social.filter((s) => s.ok).length;
      const failed = social.filter((s) => !s.ok);
      if (failed.length === 0) {
        toast.success(`All ${okCount} channels now posted`);
      } else {
        toast.error(
          `${okCount}/${social.length} ok. Failed: ${failed.map((f) => `${f.service}: ${(f.error || '').slice(0, 100)}`).join(' | ')}`,
          { duration: 12000 },
        );
      }
      await fetchPosts();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Retry failed: ${e.message}`);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-blog-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Blog</h1>
          <p className="text-muted-foreground text-sm mt-1">
            AI-generated daily posts auto-published to your site & queued to socials via Buffer.
          </p>
        </div>
        <Button onClick={fetchPosts} variant="outline" size="sm" className="gap-2">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {/* Buffer channels strip */}
      <Card>
        <CardHeader className="pb-3 flex-row items-center justify-between">
          <CardTitle className="text-base">Connected Buffer channels</CardTitle>
          <Button onClick={fetchChannels} variant="ghost" size="sm" className="gap-1.5 h-7">
            <RefreshCw className="w-3 h-3" /> Retry
          </Button>
        </CardHeader>
        <CardContent>
          {channels.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No channels detected — Buffer may be rate-limited. Click Retry in a moment.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {channels.map((c) => (
                <span
                  key={c.id}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-medium"
                >
                  <span className="capitalize">{c.service}</span>
                  <span className="text-muted-foreground font-normal">{c.name}</span>
                </span>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Generate panel */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" /> Generate a post
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="topic">Topic (optional — leave blank for AI to pick)</Label>
            <Input
              id="topic"
              placeholder="e.g. Why your café needs a one-page website"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              data-testid="admin-blog-topic-input"
            />
          </div>
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={autoPublish} onCheckedChange={setAutoPublish} data-testid="admin-blog-auto-publish" />
              Publish immediately
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={postToSocials} onCheckedChange={setPostToSocials} data-testid="admin-blog-post-socials" />
              Push to socials via Buffer
            </label>
          </div>
          <Button
            onClick={generate}
            disabled={generating}
            className="gap-2"
            data-testid="admin-blog-generate-btn"
          >
            {generating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Generating (~30s)…
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" /> Generate now
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Posts list */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">All posts ({posts.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : posts.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No posts yet. Hit "Generate now" or wait for the daily auto-run (09:00 UTC).
            </p>
          ) : (
            <div className="divide-y divide-white/5">
              {posts.map((p) => (
                <div key={p.id} className="py-4 flex items-start gap-4" data-testid={`admin-blog-row-${p.slug}`}>
                  {p.cover_image_url ? (
                    <img
                      src={p.cover_image_url.startsWith('http') ? p.cover_image_url : `${process.env.REACT_APP_BACKEND_URL}${p.cover_image_url}`}
                      alt=""
                      className="w-20 h-20 rounded-lg object-cover flex-shrink-0 border border-white/5"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-lg bg-card/60 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap text-xs mb-1">
                      <span className={`px-2 py-0.5 rounded-full font-medium ${
                        p.status === 'published'
                          ? 'bg-green-500/15 text-green-400'
                          : 'bg-amber-500/15 text-amber-400'
                      }`}>
                        {p.status}
                      </span>
                      {p.image_status === 'generating' && (
                        <span className="px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-400 font-medium">
                          generating image…
                        </span>
                      )}
                      {p.image_status === 'failed' && (
                        <span className="px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 font-medium" title={p.image_error}>
                          image failed
                        </span>
                      )}
                      {p.posted_to_socials && (
                        <span className="px-2 py-0.5 rounded-full bg-primary/15 text-primary font-medium">
                          on socials
                        </span>
                      )}
                      <span className="text-muted-foreground">{p.topic}</span>
                      <span className="text-muted-foreground">·</span>
                      <span className="text-muted-foreground">{formatDate(p.published_at || p.created_at)}</span>
                    </div>
                    <h3 className="font-semibold truncate">{p.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">{p.excerpt}</p>
                    {Array.isArray(p.buffer_post_ids) && p.buffer_post_ids.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {p.buffer_post_ids.map((b, i) => (
                          <span
                            key={i}
                            title={b.error || ''}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${
                              b.ok
                                ? 'bg-green-500/15 text-green-400'
                                : 'bg-red-500/15 text-red-400'
                            }`}
                          >
                            {b.ok ? '✓' : '✗'} {b.service}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    <a
                      href={`/blog/${p.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-white/10 hover:bg-white/5"
                      data-testid={`admin-blog-view-${p.slug}`}
                    >
                      <ExternalLink className="w-3 h-3" /> View
                    </a>
                    {p.status !== 'published' && (
                      <Button onClick={() => publish(p.id)} size="sm" variant="outline" className="gap-1.5">
                        <Send className="w-3 h-3" /> Publish
                      </Button>
                    )}
                    {p.status === 'published' && Array.isArray(p.buffer_post_ids) && p.buffer_post_ids.some((b) => !b.ok) && (
                      <Button
                        onClick={() => retrySocials(p.id)}
                        size="sm"
                        variant="outline"
                        className="gap-1.5"
                        data-testid={`admin-blog-retry-${p.slug}`}
                      >
                        <RefreshCw className="w-3 h-3" /> Retry socials
                      </Button>
                    )}
                    <Button onClick={() => remove(p.id, p.title)} size="sm" variant="ghost" className="gap-1.5 text-destructive hover:text-destructive">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BlogAdmin;
